import React, { useEffect } from 'react';
import { graphql, PageProps } from 'gatsby';

import createOpenGraphTags from '@repo/utils/createOpenGraphTags';

import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import Button from '@repo/ui-components/button/Button';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import { GetSanitySharedPageQuery } from '@repo/models/gatsby-graphql-types';

import Layout from '@components/layout';
import getInternalLink from '@components/utils/getInternalLink';
import theme from '@components/theme';

interface Optanon {
  LoadBanner: () => void;
}

declare global {
  interface Window {
    Optanon?: Optanon;
  }
}

interface SanitySharedPageProps extends PageProps {
  data: GetSanitySharedPageQuery;
}

function SanitySharedPage({ data, location }: Readonly<SanitySharedPageProps>) {
  const { sanitySharedPage } = data;

  useEffect(() => {
    // cookiepro - triggers content load for SPA page transitions
    if (window?.Optanon) {
      window?.Optanon?.LoadBanner();
    }
  }, []);

  return (
    <Layout
      location={location}
      headTagData={{
        title: sanitySharedPage?.SEOMetadata?.title || sanitySharedPage?.title,
        description:
          sanitySharedPage?.SEOMetadata?.description ||
          sanitySharedPage?.description,
        canonicalUrl: sanitySharedPage?.SEOMetadata?.canonicalUrl,
        noIndex: sanitySharedPage?.SEOMetadata?.noIndex,
        noFollow: sanitySharedPage?.SEOMetadata?.noFollow,
        meta: createOpenGraphTags(
          {
            title:
              sanitySharedPage?.SEOMetadata?.ogTitle ||
              sanitySharedPage?.SEOMetadata?.title ||
              sanitySharedPage?.title,
            description:
              sanitySharedPage?.SEOMetadata?.ogDescription ||
              sanitySharedPage?.SEOMetadata?.description ||
              sanitySharedPage?.description,
            url:
              sanitySharedPage?.SEOMetadata?.ogUrl ||
              sanitySharedPage?.SEOMetadata?.canonicalUrl ||
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              '/',
            image:
              sanitySharedPage?.SEOMetadata?.ogImage || sanitySharedPage?.image,
          },
          'invisiblefence'
        ),
      }}
      fullWidth
    >
      <PageWrapper background="none" padding="0">
        <BreadcrumbWithWrapper
          elements={[
            {
              text: 'Home',
              link: '/',
            },
            {
              text: sanitySharedPage.title,
            },
          ]}
        />
      </PageWrapper>
      <PageWrapper
        background={
          sanitySharedPage?.includePageBackground
            ? theme.colors.background
            : 'inherit'
        }
      >
        {sanitySharedPage?.blockBuilder?.blocks && (
          <BlockBuilder
            blocks={sanitySharedPage?.blockBuilder?.blocks}
            getInternalLink={getInternalLink}
            Button={Button}
            brand="petsafe"
          />
        )}

        {location.pathname.includes('privacy-policy') && (
          <div id="ot-sdk-cookie-policy" />
        )}
      </PageWrapper>
    </Layout>
  );
}

export const query = graphql`
  query GetSanitySharedPage($id: String!) {
    sanitySharedPage(
      id: { eq: $id }
      siteIdentifier: { in: ["global", "ifb"] }
    ) {
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      title
      image: _rawImage(resolveReferences: { maxDepth: 3 })
      description
      blockBuilder {
        blocks: _rawBlocks(resolveReferences: { maxDepth: 13 })
      }
      includePageBackground
    }
  }
`;

export default SanitySharedPage;
